<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      show-icon
      title="入驻申请通过后会自动创建资源主体（来源=apply，已认领且已认证，归属申请人）；驳回需填写原因。"
    />
    <el-form :inline="true" :model="query" class="-mb-15px">
      <el-form-item label="审核状态">
        <el-select v-model="query.status" clearable class="!w-140px" placeholder="全部">
          <el-option label="待审核" value="pending" />
          <el-option label="已通过" value="approved" />
          <el-option label="已驳回" value="rejected" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">搜索</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="申请主体" min-width="200">
        <template #default="{ row }">
          <div class="min-w-0">
            <div class="font-500 truncate">
              {{ row.name }}
              <el-tag v-if="row.resourceId" class="ml-5px" size="small" type="success">
                已生成资源
              </el-tag>
            </div>
            <div class="text-12px text-#909399 truncate">
              {{ row.shortName || '-' }} · {{ row.industry || '未填行业' }}
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="creditCode" label="信用代码" min-width="170" show-overflow-tooltip />
      <el-table-column prop="city" label="城市" width="110" />
      <el-table-column prop="contact" label="联系人" width="110" />
      <el-table-column prop="mobile" label="手机号" width="130" />
      <el-table-column label="材料" min-width="160">
        <template #default="{ row }">
          <el-space wrap>
            <el-tag v-if="row.license" size="small" type="primary">营业执照</el-tag>
            <el-tag v-if="row.photo" size="small">形象照</el-tag>
            <span v-if="!row.license && !row.photo" class="text-12px text-#909399">未上传</span>
          </el-space>
        </template>
      </el-table-column>
      <el-table-column label="审核状态" width="100">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="rejectReason" label="驳回原因" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.rejectReason || '-' }}</template>
      </el-table-column>
      <el-table-column label="申请时间" width="170">
        <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <template v-if="row.status === 'pending'">
            <el-button
              v-hasPermi="['bosscircle:resource:audit']"
              link
              type="success"
              @click="handleApprove(row)"
            >
              通过
            </el-button>
            <el-button
              v-hasPermi="['bosscircle:resource:audit']"
              link
              type="danger"
              @click="openReject(row)"
            >
              驳回
            </el-button>
          </template>
          <span v-else class="text-12px text-#909399">已处理</span>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog title="入驻申请详情" v-model="detailVisible" width="640px">
    <el-descriptions v-if="current?.id" :column="2" border>
      <el-descriptions-item label="名称">{{ current.name }}</el-descriptions-item>
      <el-descriptions-item label="简称">{{ current.shortName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="信用代码">{{ current.creditCode || '-' }}</el-descriptions-item>
      <el-descriptions-item label="行业">{{ current.industry || '-' }}</el-descriptions-item>
      <el-descriptions-item label="城市">{{ current.city || '-' }}</el-descriptions-item>
      <el-descriptions-item label="联系人">{{ current.contact || '-' }}</el-descriptions-item>
      <el-descriptions-item label="手机号">{{ current.mobile || '-' }}</el-descriptions-item>
      <el-descriptions-item label="申请人ID">{{ current.userId }}</el-descriptions-item>
      <el-descriptions-item label="审核状态">
        <el-tag :type="statusTag(current.status)" size="small">
          {{ statusText(current.status) }}
        </el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="生成资源">
        {{ current.resourceId || '未生成' }}
      </el-descriptions-item>
      <el-descriptions-item label="申请时间">
        {{ formatTime(current.createTime) }}
      </el-descriptions-item>
      <el-descriptions-item label="审核时间">
        {{ formatTime(current.auditTime) }}
      </el-descriptions-item>
      <el-descriptions-item label="驳回原因" :span="2">
        {{ current.rejectReason || '-' }}
      </el-descriptions-item>
      <el-descriptions-item label="简介" :span="2">{{ current.intro || '-' }}</el-descriptions-item>
    </el-descriptions>
    <div class="mt-12px flex flex-wrap gap-16px">
      <div v-for="material in detailMaterials" :key="material.label" class="w-200px">
        <div class="mb-8px text-14px text-#909399">{{ material.label }}</div>
        <el-image
          v-if="isImageUrl(material.value)"
          :src="material.value"
          :preview-src-list="[material.value]"
          preview-teleported
          fit="contain"
          class="h-140px w-200px border border-solid border-[var(--el-border-color)]"
        />
        <div v-else-if="material.value" class="text-12px text-#909399">
          已提交（历史数据未存图片地址）
        </div>
        <div v-else class="text-12px text-#909399">未上传</div>
      </div>
    </div>
    <template #footer>
      <el-button @click="detailVisible = false">关 闭</el-button>
      <el-button
        v-if="current?.status === 'pending'"
        v-hasPermi="['bosscircle:resource:audit']"
        type="success"
        @click="handleApprove(current)"
      >
        通过并创建资源
      </el-button>
    </template>
  </Dialog>

  <Dialog title="驳回入驻申请" v-model="rejectVisible" width="500px">
    <el-form ref="rejectFormRef" :model="rejectForm" :rules="rejectRules" label-width="90px">
      <el-form-item label="主体">{{ current?.name }}</el-form-item>
      <el-form-item label="驳回原因" prop="rejectReason">
        <el-input
          v-model="rejectForm.rejectReason"
          type="textarea"
          :rows="3"
          placeholder="请填写驳回原因，将展示给申请人"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="rejectVisible = false">取 消</el-button>
      <el-button :loading="rejecting" type="danger" @click="submitReject">确定驳回</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { formatDate } from '@/utils/formatTime'
import * as Api from '@/api/business/resource'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'BossCircleResourceApply' })

const route = useRoute()
const message = useMessage()

const loading = ref(false)
const list = ref<Api.ResourceApplyVO[]>([])
const total = ref(0)
const current = ref<Api.ResourceApplyVO>()
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  status: (route.query.status as Api.ResourceAuditStatus) || undefined
})

const statusText = (s?: string) =>
  ({ pending: '待审核', approved: '已通过', rejected: '已驳回' } as Record<string, string>)[s ?? ''] || '-'
const statusTag = (s?: string) =>
  s === 'approved' ? 'success' : s === 'rejected' ? 'danger' : 'warning'

const formatTime = (value?: string | number) => (value ? formatDate(value) : '-')
/** 材料列的历史值是 'uploaded' 这类占位串，只有看着像地址的才当图片渲染，否则会出破图 */
const isImageUrl = (value?: string) =>
  !!value && /^(https?:\/\/|\/|data:image\/)/i.test(value.trim())

const load = async () => {
  loading.value = true
  try {
    const params: any = { pageNo: query.pageNo, pageSize: query.pageSize }
    if (query.status) params.status = query.status
    const data = await Api.getResourceApplyPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  query.pageNo = 1
  load()
}

const reset = () => {
  query.status = undefined
  search()
}

const handleApprove = async (row: Api.ResourceApplyVO) => {
  await message.confirm(`确认通过「${row.name}」的入驻申请吗？通过后将自动创建资源主体。`)
  await Api.auditResourceApply({ id: row.id!, approved: true })
  message.success('已通过，已生成资源主体')
  detailVisible.value = false
  load()
}

const detailVisible = ref(false)

const rejectVisible = ref(false)
const rejecting = ref(false)
const rejectFormRef = ref<FormInstance>()
const rejectForm = reactive<{ rejectReason: string }>({ rejectReason: '' })
const rejectRules = {
  rejectReason: [{ required: true, message: '驳回原因不能为空', trigger: 'blur' }]
} as FormRules

const openReject = (row: Api.ResourceApplyVO) => {
  current.value = row
  rejectForm.rejectReason = ''
  rejectVisible.value = true
  nextTick(() => rejectFormRef.value?.clearValidate())
}

const submitReject = async () => {
  await rejectFormRef.value?.validate()
  rejecting.value = true
  try {
    await Api.auditResourceApply({
      id: current.value!.id!,
      approved: false,
      rejectReason: rejectForm.rejectReason.trim()
    })
    message.success('已驳回')
    rejectVisible.value = false
    load()
  } finally {
    rejecting.value = false
  }
}

const MATERIAL_FIELDS: Array<{ key: 'license' | 'photo'; label: string }> = [
  { key: 'license', label: '营业执照' },
  { key: 'photo', label: '形象照' }
]
const detailMaterials = computed(() =>
  MATERIAL_FIELDS.map((field) => ({
    label: field.label,
    value: current.value?.[field.key]
  }))
)

const openDetail = (row: Api.ResourceApplyVO) => {
  current.value = row
  detailVisible.value = true
}

onMounted(load)
</script>